import { Injectable } from '@nestjs/common';
import { InjectDataSource, InjectRepository } from '@nestjs/typeorm';
import { Repository, In } from 'typeorm';
import { DayMonthPoOperationSummaryModel, DayMonthPoOperationSummaryResponse, DaySummaryModel, GlobalResponseObject, MonthSummaryModel, OpCategoryWiseSummaryQtyModel, OpCategoryWiseSummaryQtyResponse, OpWiseSummaryQtyModel, OperationCategoryDisplayValues, OperationCategoryEnum, PoInfoModel, PoOperationCompletionModel, PoStatusEnum, PoSummaryForOperationModel, ScheduleWiseOpCompletionModel, UnitDayWeekMonthRequest, UnitModel, UnitResponse } from '@shahi-analytics/shared-models';
import { BuyerEntity } from '../entities/buyer.entity';
import { PoHeaderEntity } from '../entities/po-header.entity';
import { PoLineEntity } from '../entities/po-line.entity';
import { ScheduleEntity } from '../entities/schedule.entity';
import { TrackingInfoEntity } from '../entities/tracking-info.entity';
import { StyleOperationEntity } from '../entities/style-op.entity';
import { OperationEntity } from '../entities/operation.entity';
import { DataSource } from 'typeorm';
import * as moment from "moment";
import { ErrorResponse } from '@shahi-analytics/backend-utils';
import configuration from '../../config/configuration';
import { UnitEntity } from '../entities/unit.entity';

/**
 * Things to Remember 
 * Day wise left block
 * 
 * FINISHING, WASHING :
 * WIP: The total input qty of operation till date - The total input qty of operation till date
 * INTRANSIT: The total output qty of last operation - The total input qty of first operation of current op category till date
 * 
 * SEWING : 
 * WIP: The total DC qty till date - The total sewing out qty till date
 * INTRANSIT: Same as WIP
 * 
 * HitRate: total day output/total hours spent on that day
 */
@Injectable()
export class GtsService {
  private poHeaderEnt: Repository<PoHeaderEntity>;
  private poLineEnt: Repository<PoLineEntity>;
  private schEnt: Repository<ScheduleEntity>;
  private trackInfoEnt: Repository<TrackingInfoEntity>;
  private lineActualDayTrackEnt: Repository<BuyerEntity>;
  private styleOpEnt: Repository<StyleOperationEntity>;
  private opEnt: Repository<OperationEntity>;
  private unitEnt: Repository<UnitEntity>;
  private STYLE_CONST: string;
  constructor(
    @InjectDataSource() private buyerEnt: Repository<BuyerEntity>,
    @InjectDataSource() private dataSource: DataSource,
  ) {
    this.opEnt = dataSource.getRepository(OperationEntity);
    this.poHeaderEnt = dataSource.getRepository(PoHeaderEntity);
    this.poLineEnt = dataSource.getRepository(PoLineEntity);
    this.schEnt = dataSource.getRepository(ScheduleEntity);
    this.trackInfoEnt = dataSource.getRepository(TrackingInfoEntity);
    this.styleOpEnt = dataSource.getRepository(StyleOperationEntity);
    this.unitEnt = dataSource.getRepository(UnitEntity);
    this.STYLE_CONST = 'STYLE1';
  }


  async getOpCategoryWiseSummaryQtysTillDate(unitId: string, date: string): Promise<OpCategoryWiseSummaryQtyResponse> {
    const queryToGetQtyTillDate: {qty: number, op_code: string, op_category: string, op_sequence: number}[] = await this.trackInfoEnt.createQueryBuilder('track')
    .select('sum(good_quantity) as qty, op_code, op_category, op_sequence')
    .where(`unit_id = '${unitId}'`)
    .groupBy('op_code')
    .orderBy('op_sequence')
    .getRawMany();

    const queryToGetQtyDate: {qty: number, op_code: string, op_category: string, op_sequence: number}[] = await this.trackInfoEnt.createQueryBuilder('track')
    .select('sum(good_quantity)as qty, op_code, op_category, op_sequence')
    .where(`unit_id = '${unitId}' AND date = '${date}'`)
    .groupBy('op_code')
    .orderBy('op_sequence')
    .getRawMany();

    if(queryToGetQtyDate.length == 0) {
      // throw new ErrorResponse(0, 'Today operation data is not found');
    }

    const opsCatOpsCodeMap = await this.getTheFirstAndLastOperationsForOpCategory([this.STYLE_CONST], unitId);


    const qtySummaryObjsForCategories: OpCategoryWiseSummaryQtyModel[] = [];

    for (const [style, opsCatInfo] of opsCatOpsCodeMap) {
      for (const [opsCategory, opsInfo] of opsCatInfo) {
        const firstOpsDataTillDate = queryToGetQtyTillDate?.find(opsData => opsData.op_code == opsInfo.firstOp);
        const lastOpsDataTillDate = queryToGetQtyTillDate?.find(opsData => opsData.op_code == opsInfo.lastOp);
        const totalWorkingHours = 0;
        const qtySummaryInfo = []
        const qtySummary = queryToGetQtyTillDate?.filter(qtyInfo => qtyInfo.op_code == opsInfo.firstOp  || qtyInfo.op_code == opsInfo.lastOp);
        // if(opsCategory == OperationCategoryEnum.SEWING) {
        //   // push the cutting op as the first op for the sewing. Because first op for the sewing is the total DC in
        //   const lastOpsDataOfCutting = opsCatInfo.get(OperationCategoryEnum.CUTTING);
        //   const tillDateOpsQty = queryToGetQtyDate?.find(qty => qty.op_code == lastOpsDataOfCutting.lastOp);
        //   const eachOpsSummary =  new OpWiseSummaryQtyModel(tillDateOpsQty?.op_code ?? '1', opsCategory,  tillDateOpsQty?.qty ?? 0, tillDateOpsQty ? tillDateOpsQty.qty : 0, 0);
        //   qtySummaryInfo.push(eachOpsSummary);
        // }
        for (const eachOps of qtySummary) {
          const tillDateOpsQty = queryToGetQtyDate?.find(qty => qty.op_code == eachOps.op_code);
          const eachOpsSummary =  new OpWiseSummaryQtyModel(eachOps.op_code, opsCategory,  eachOps.qty, tillDateOpsQty ? tillDateOpsQty.qty : 0, 0);
          qtySummaryInfo.push(eachOpsSummary);
        }
        if (opsCategory == OperationCategoryEnum.CUTTING) {
          const opCatWiseSummary = new OpCategoryWiseSummaryQtyModel(opsCategory, 0, 0, totalWorkingHours, qtySummaryInfo);
          qtySummaryObjsForCategories.push(opCatWiseSummary);
        } else if (opsCategory == OperationCategoryEnum.WASHING) {
          const lastOpsDataOfSewing = opsCatInfo.get(OperationCategoryEnum.SEWING);
          const lastInputData = queryToGetQtyTillDate?.find(qty => qty.op_code == lastOpsDataOfSewing.lastOp);
          const inTransit = Number(lastInputData?.qty ?? 0) - Number(firstOpsDataTillDate?.qty ?? 0);
          const wipQty = Number(firstOpsDataTillDate?.qty ?? 0) - Number(lastOpsDataTillDate?.qty ?? 0);
          const opCatWiseSummary = new OpCategoryWiseSummaryQtyModel(opsCategory, wipQty, inTransit, totalWorkingHours, qtySummaryInfo);
          qtySummaryObjsForCategories.push(opCatWiseSummary);
        } else if (opsCategory == OperationCategoryEnum.FINISHING){
          const lastOpsDataOfWashing = opsCatInfo.get(OperationCategoryEnum.WASHING);
          const lastInputData = queryToGetQtyTillDate?.find(qty => qty.op_code == lastOpsDataOfWashing.lastOp);
          const inTransit = Number(lastInputData?.qty ?? 0) - Number(firstOpsDataTillDate?.qty ?? 0);
          const wipQty = Number(firstOpsDataTillDate?.qty ?? 0) - Number(lastOpsDataTillDate?.qty ?? 0);
          const opCatWiseSummary = new OpCategoryWiseSummaryQtyModel(opsCategory, wipQty, inTransit, totalWorkingHours, qtySummaryInfo);
          qtySummaryObjsForCategories.push(opCatWiseSummary);
        } else if (opsCategory == OperationCategoryEnum.SEWING) {
          const lastOpsDataOfCutting = opsCatInfo.get(OperationCategoryEnum.CUTTING);
          const lastInputData = queryToGetQtyTillDate?.find(qty => qty.op_code == lastOpsDataOfCutting.lastOp);
          const inTransit = Number(lastInputData?.qty ?? 0) - Number(firstOpsDataTillDate?.qty ?? 0);
          const wipQty = Number(firstOpsDataTillDate?.qty ?? 0) - Number(lastOpsDataTillDate?.qty ?? 0);
          const opCatWiseSummary = new OpCategoryWiseSummaryQtyModel(opsCategory, wipQty, inTransit, totalWorkingHours, qtySummaryInfo);
          qtySummaryObjsForCategories.push(opCatWiseSummary);
        }
      } 
    }
    return new OpCategoryWiseSummaryQtyResponse(true, 0, '', qtySummaryObjsForCategories);
  }

  /**
   * 
   * @param unitId 
   * @param date 
   */
  async getScheduleWiseOpSummary(unitId: string, date: string, opCategory?: OperationCategoryEnum): Promise<DayMonthPoOperationSummaryResponse> {
    // let date = new Date();
    const currDate = new Date(date);
    const currentDate = date; //date.toJSON().slice(0, 10);
    const currMonth = currDate.getMonth();
    const currYear = currDate.getFullYear();
    const day = currDate.getDate();
    const paddedMonth = (currMonth).toString().padStart(2,'0');
    const marginDate = currYear+'-'+paddedMonth+'-'+day;
    const considerMarginDate = configuration().appSepcific.considerMarginDate;
    // return null;
    // get all the POs for the delivery dates
    const query = this.schEnt.createQueryBuilder('sch')
      .select(`sch.id, sch.schedule, sch.style, sch.color, sch.quantity, sch.destination, sch.delivery_date, buy.buyer_name, poh.po_description`)
      .leftJoin(PoHeaderEntity, 'poh', 'sch.po_header_id = poh.id AND sch.unit_id = poh.unit_id ')
      .leftJoin(BuyerEntity, 'buy', 'buy.id = poh.buyer_id AND buy.unit_id = poh.unit_id')
      .where(`sch.unit_id = '${unitId}'  AND sch.po_status IN (:...poStatus)`, { poStatus: [PoStatusEnum.INPROGRESS, PoStatusEnum.OPEN] });
      if(considerMarginDate) {
        query.andWhere(`sch.created_at > '${marginDate} 00:00:00'`);
      }
      query.orderBy(`sch.delivery_date`, 'ASC')
      .addOrderBy(`poh.po_number`, 'ASC')
      .addOrderBy(`sch.destination`, 'ASC')
    const schedulesForPo: { id: number, po_description: string, schedule: string, style: string, color: string, quantity: number, destination: string, delivery_date: string, buyer_name: string }[]
      = await query.getRawMany();
    // const schedulesForPo = await this.schEnt.find({ select: ['id', 'poHeaderId'], where: { unitId: unitId, poStatus: In([PoStatusEnum.OPEN, PoStatusEnum.INPROGRESS])} });
    if (schedulesForPo.length == 0) {
      throw new ErrorResponse(0, 'Open POs not found');
    }

    // filter out all the styles
    const styles = new Set<string>();
    const poSchIds: number[] = [];
    schedulesForPo.forEach(r => {
      styles.add(r.style);
      poSchIds.push(r.id);
    });
    // console.log(styles);
    // console.log(poSchIds);
    // return null;
    // get the fisrt and last operations for the style and the op category
    // const firstAndLastOps = await this.getTheFirstAndLastOperationsForStyleInOpCategory(Array.from(styles), unitId);
    const firstAndLastOps = await this.getTheFirstAndLastOperationsForOpCategory(Array.from(styles), unitId);
    // get the operations related info
    const opInfo = await this.getOpInfo(unitId);

    const poSummaryModels: PoSummaryForOperationModel[] = [];
    for (const poSchRec of schedulesForPo) {
      const opQtys: PoOperationCompletionModel[] = await this.getScheduleOpScanQtysTillDate(date, unitId, Array.from(styles), poSchRec.id, firstAndLastOps, opInfo);
      const copiedOpQtys: PoOperationCompletionModel[] = [];
      opQtys.forEach(r => {
        if(r.todayInputQty > 0 || r.todayOutputQty > 0 || r.opWipTillNow > 0) {
          copiedOpQtys.push(r);
        }
      })
      const poSummaryModel = new PoSummaryForOperationModel(poSchRec.po_description, poSchRec.style, poSchRec.schedule, poSchRec.buyer_name, poSchRec.destination, poSchRec.delivery_date, poSchRec.quantity, copiedOpQtys);
      poSummaryModels.push(poSummaryModel);
    }
    if (poSchIds.length == 0) {
      throw new ErrorResponse(0, 'No data found for the day')
    }
    // get the current month summary
    const currentMonthOpCompletionSummary = await this.getScheduleOpScanQtysForMonth(unitId, poSchIds, firstAndLastOps, opInfo, currMonth, currYear);
    const monthSummaryModel = new MonthSummaryModel(currMonth, currentMonthOpCompletionSummary);
    // get the today summary
    const currentDateOpCompletionSummary = await this.getScheduleOpScanQtysForDate(unitId, Array.from(styles), poSchIds, firstAndLastOps, opInfo, currentDate);
    const poListsForCurrentDay = await this.getPoListForTheDate(poSchIds, currentDate, unitId);
    const dayWiseSummary = new DaySummaryModel(currentDate, poListsForCurrentDay, currentDateOpCompletionSummary);
    return new DayMonthPoOperationSummaryResponse(true, 0, '', new DayMonthPoOperationSummaryModel(poSummaryModels, monthSummaryModel, dayWiseSummary));
  }

  /**
   * 
   * @param unitId 
   * @param poSchId 
   * @param firstAndLastOps 
   * @param opInfo 
   * @returns 
   */
  async getScheduleOpScanQtysTillDate(currentDate: string, unitId: string, styles: string[], poSchId: number, firstAndLastOps: Map<string, Map<string, { firstOp: string, lastOp: string }>>, opInfo: Map<string, OperationEntity>, opCategory?: OperationCategoryEnum): Promise<PoOperationCompletionModel[]> {
    // --------------- BLOCK 1 ---------------------------
    // get the cumulative qty for each record po + schedule 
    // for entire PO till date
    const scheduleCompQtysTillNowQuery = this.trackInfoEnt.createQueryBuilder('track')
      .select('schedule, color, GROUP_CONCAT(DISTINCT po_schedule_id) as po_sch_ids, style, SUM(good_quantity) as good, SUM(rejected_quantity) as rej, SUM(rework_quantity) as rework, op_code, op_category, op_sequence, count(DISTINCT date) as total_worked_dates, MIN(timestamp) as first_scan, MAX(timestamp) as last_scan')
      .where(` unit_id = '${unitId}' AND po_schedule_id = ${poSchId} `);
    if (opCategory) {
      scheduleCompQtysTillNowQuery.andWhere(` op_category = '${opCategory}' `);
    }
    scheduleCompQtysTillNowQuery.groupBy('unit_id, schedule, style, color, op_code, op_category')
      .orderBy('op_sequence');

    const scheduleCompQtysTillNowResult: { schedule: string, color: string, style: string, good: number, rej: number, rework: number, op_code: string, op_category: string, op_sequence: number, total_worked_dates: number, first_scan: string, last_scan: string, po_sch_ids: string }[] = await scheduleCompQtysTillNowQuery.getRawMany();

    const opCategoryOpInfoModel = new Map<string, PoOperationCompletionModel>();
    const todayInputOutPutMap = new Map<string, number>();


    // query to get the today scan in and scan out for the above result
    const scheduleCompQtysTillNowQueryForDate = this.trackInfoEnt.createQueryBuilder('track')
      .select('schedule, po_schedule_id, style, color, SUM(good_quantity) as good, SUM(rejected_quantity) as rej, SUM(rework_quantity) as rework, op_code, op_category, op_sequence, count(distinct hour) as total_hours, MIN(timestamp) as first_scan, MAX(timestamp) as last_scan')
      .where(` unit_id = '${unitId}' AND date = '${currentDate}' AND po_schedule_id = ${poSchId} `);
    if (opCategory) {
      scheduleCompQtysTillNowQueryForDate.andWhere(` op_category = '${opCategory}' `);
    }
    scheduleCompQtysTillNowQueryForDate.groupBy('unit_id, schedule, style, color, op_code, op_category')
    .orderBy('op_sequence');

    const scheduleCompQtysTodayResult: { schedule: string, po_schedule_id: number, style: string, color: string, good: number, rej: number, rework: number, op_code: string, op_category: string, op_sequence: number, total_hours: number, first_scan: string, last_scan: string }[] = await scheduleCompQtysTillNowQueryForDate.getRawMany();

    for (const eachDayRecord of scheduleCompQtysTodayResult) {
      const combKey = `${poSchId}$$${eachDayRecord.style}$$${eachDayRecord.color}$$${eachDayRecord.op_code}`;
      if (!todayInputOutPutMap.has(combKey)) {
        todayInputOutPutMap.set(combKey, Number(eachDayRecord.good) + Number(eachDayRecord.rej));
      } else {
        console.log('REC already exist');
      }
    }

    const opCategorySet = new Set<string>();
    for (const rec of scheduleCompQtysTillNowResult) {
      const combKey = `${poSchId}$$${rec.style}$$${rec.color}$$${rec.op_code}`;

      const qty = todayInputOutPutMap?.get(combKey) ? todayInputOutPutMap?.get(combKey) : 0;

      rec.good = Number(rec.good);

      const currStyle = rec.style;
      const currOpCategory = rec.op_category;
      opCategorySet.add(currOpCategory);
      const currOpInfo = opInfo.get(rec.op_code.toString());
      // TODO
      if (!currOpInfo) {
        continue;
      }
      if (!opCategoryOpInfoModel.has(rec.op_category)) {
        const opSummaryModel = new PoOperationCompletionModel(rec.op_category, rec.op_code, currOpInfo.opCategory, currOpInfo.opSequence, 0, 0, 0, 0, 0, 0, 0, 0, null, null, 0);
        opCategoryOpInfoModel.set(currOpCategory, opSummaryModel);
      }

      // const firstOp =  firstAndLastOps?.get(currStyle)?.get(currOpCategory)?.firstOp;
      // const lastOp = firstAndLastOps?.get(currStyle)?.get(currOpCategory)?.lastOp;

      const firstOp = firstAndLastOps?.get(this.STYLE_CONST)?.get(currOpCategory)?.firstOp;
      const lastOp = firstAndLastOps?.get(this.STYLE_CONST)?.get(currOpCategory)?.lastOp;

      if (rec.op_code == firstOp) {
        opCategoryOpInfoModel.get(currOpCategory).totalInputQty += rec.good;
        opCategoryOpInfoModel.get(currOpCategory).todayInputQty += qty;
        // TODO
        opCategoryOpInfoModel.get(currOpCategory).firstInputScanDateTime = rec.last_scan;
      } else if (rec.op_code == lastOp) {
        opCategoryOpInfoModel.get(currOpCategory).todayOutputQty += qty;
        // rec.total_worked_dates = rec.total_worked_dates > 0 ? rec.total_worked_dates : 1;
        const totalHours = rec.total_worked_dates * 7.5;
        opCategoryOpInfoModel.get(currOpCategory).totalOutPutQty += rec.good;
        opCategoryOpInfoModel.get(currOpCategory).hitRatePerDay = rec.total_worked_dates > 0 ? Number((rec.good / rec.total_worked_dates).toFixed(2)) : 0;
        opCategoryOpInfoModel.get(currOpCategory).hitRatePerHour = totalHours > 0 ? Number((rec.good / totalHours).toFixed(2)) : 0;
        // console.log(Number(rec.last_scan);
        opCategoryOpInfoModel.get(currOpCategory).lastInputScanDateTime = rec.last_scan;
      } else {
        // console.log('OP not found');
        // console.log(firstOp + ' : ' + lastOp + ' : ' + rec.op_code);
      }
    }

    let schIds = [poSchId];
    const schIdsArr = schIds.map(r => Number(r));
    const wipMap = await this.getWip(unitId, styles, firstAndLastOps, schIdsArr);
    opCategorySet.forEach(currOpCategory => {
      if(opCategoryOpInfoModel.has(opCategory)) {
        const opQtys = wipMap.get(currOpCategory);
        opCategoryOpInfoModel.get(currOpCategory).opWipTillNow = opQtys.totalInput - opQtys.totalOutput;
      }
    })

    const poOpCompModels: PoOperationCompletionModel[] = [];
    opCategoryOpInfoModel.forEach(r => {
      r.lastInputScanDateTime = r.lastInputScanDateTime ? moment(Number(r.lastInputScanDateTime)).format('YYYY-MM-DD HH:MM') : null;
      r.firstInputScanDateTime = r.firstInputScanDateTime ? moment(Number(r.firstInputScanDateTime)).format('YYYY-MM-DD HH:MM') : null;
      poOpCompModels.push(r);
    });
    return poOpCompModels;
  }

  /**
   * 
   * @param unitId 
   * @param poSchIds 
   * @param firstAndLastOps 
   * @param opInfo 
   * @param date 
   * @returns 
   */
  async getScheduleOpScanQtysForDate(unitId: string, styles: string[], poSchIds: number[], firstAndLastOps: Map<string, Map<string, { firstOp: string, lastOp: string }>>, opInfo: Map<string, OperationEntity>, date: string, opCategory?: OperationCategoryEnum): Promise<PoOperationCompletionModel[]> {

    const filteredPoSchIds: number[] = [];
    // --------------- BLOCK 2 ---------------------------
    // for entire PO for the date
    const scheduleCompQtysTillNowQuery = this.trackInfoEnt.createQueryBuilder('track')
      .select('po_schedule_id, style, SUM(good_quantity) as good, SUM(rejected_quantity) as rej, SUM(rework_quantity) as rework, op_code, op_category, op_sequence, count(distinct hour) as total_hours, MIN(timestamp) as first_scan, MAX(timestamp) as last_scan')
      .where(` unit_id = '${unitId}' AND po_schedule_id IN(:...schIds) AND date = '${date}' `, { schIds: poSchIds });
    if (opCategory) {
      scheduleCompQtysTillNowQuery.andWhere(` op_category = '${opCategory}' `);
    }
    // scheduleCompQtysTillNowQuery.groupBy('unit_id, schedule, style, color, op_code, op_category')
    scheduleCompQtysTillNowQuery.groupBy('unit_id, op_code, op_category')
    .orderBy('op_sequence');
    const scheduleCompQtysTillNowResult: { po_schedule_id: number, style: string, good: number, rej: number, rework: number, op_code: string, op_category: string, op_sequence: number, total_hours: number, first_scan: string, last_scan: string }[] = await scheduleCompQtysTillNowQuery.getRawMany();
    const opCategoryOpInfoModel = new Map<string, PoOperationCompletionModel>();

    for (const rec of scheduleCompQtysTillNowResult) {
      // NOTE: check if the hours are > 8 then make them  = 8 
      rec.total_hours = Number(rec.total_hours) ? Number(rec.total_hours) > 8 ? 8 : Number(rec.total_hours) : 0;

      filteredPoSchIds.push(rec.po_schedule_id);
      rec.good = Number(rec.good);
      const currStyle = rec.style;
      const currOpCategory = rec.op_category;
      const currOpInfo = opInfo.get(rec.op_code);
      if (!opCategoryOpInfoModel.has(rec.op_category)) {
        const opSummaryModel = new PoOperationCompletionModel(rec.op_category, rec.op_code, currOpInfo.opCategory, currOpInfo.opSequence, 0, 0, 0, 0, 0, 0, 0, 0, '0', '0', 0);
        opCategoryOpInfoModel.set(currOpCategory, opSummaryModel);
      }

      // const firstOp =  firstAndLastOps.get(currStyle)?.get(currOpCategory)?.firstOp;
      // const lastOp = firstAndLastOps.get(currStyle)?.get(currOpCategory)?.lastOp;

      const firstOp = firstAndLastOps.get(this.STYLE_CONST)?.get(currOpCategory)?.firstOp;
      const lastOp = firstAndLastOps.get(this.STYLE_CONST)?.get(currOpCategory)?.lastOp;

      if (rec.op_code == firstOp) {
        opCategoryOpInfoModel.get(currOpCategory).todayInputQty += rec.good;
        // TODO
        const lastScan = opCategoryOpInfoModel.get(currOpCategory).firstInputScanDateTime;
        const finalLastScan = Math.max(Number(rec.last_scan), Number(lastScan));
        opCategoryOpInfoModel.get(currOpCategory).firstInputScanDateTime = finalLastScan.toString();

        if(rec.op_category == OperationCategoryEnum.SEWING) {
          opCategoryOpInfoModel.get(currOpCategory).todayOutputQty += rec.good;
          opCategoryOpInfoModel.get(currOpCategory).hitRatePerHour = rec.total_hours > 0 ? Number((rec.good / rec.total_hours).toFixed(2)) : 0;
          // TODO
          const lastScan = opCategoryOpInfoModel.get(currOpCategory).lastInputScanDateTime;
          const finalLastScan = Math.max(Number(rec.last_scan), Number(lastScan));
          opCategoryOpInfoModel.get(currOpCategory).lastInputScanDateTime = finalLastScan.toString();
        }

      } else if (rec.op_code == lastOp) {
        opCategoryOpInfoModel.get(currOpCategory).todayOutputQty += rec.good;
        opCategoryOpInfoModel.get(currOpCategory).hitRatePerHour = rec.total_hours > 0 ? Number((rec.good / rec.total_hours).toFixed(2)) : 0;
        // TODO
        const lastScan = opCategoryOpInfoModel.get(currOpCategory).lastInputScanDateTime;
        const finalLastScan = Math.max(Number(rec.last_scan), Number(lastScan));
        opCategoryOpInfoModel.get(currOpCategory).lastInputScanDateTime = finalLastScan.toString();
      }
    }
    // const wipMap = await this.getWip(unitId, styles, firstAndLastOps, filteredPoSchIds);
    const poOpCompModels: PoOperationCompletionModel[] = [];
    opCategoryOpInfoModel.forEach(r => {
      // const opCat = r.operationCategory;
      // const opQtys = wipMap.get(opCat);
      // // get the WIP for the op category
      // r.inTransitQty = opQtys.inTransitQty;
      // r.opWipTillNow = (opQtys.totalInput - opQtys.totalOutput); // > 0 ? (opQtys.totalInput - opQtys.totalOutput) : 0;
      r.lastInputScanDateTime = r.lastInputScanDateTime ? moment(Number(r.lastInputScanDateTime)).format('YYYY-MM-DD HH:MM') : null;
      r.firstInputScanDateTime = r.firstInputScanDateTime ? moment(Number(r.firstInputScanDateTime)).format('YYYY-MM-DD HH:MM') : null;
      poOpCompModels.push(r);
    });
    return poOpCompModels;
  }

  /**
  * 
  * @param unitId 
  * @param poSchIds 
  * @param firstAndLastOps 
  * @param opInfo 
  * @param date 
  * @returns 
  */
  async getScheduleOpScanQtysForMonth(unitId: string, poSchIds: number[], firstAndLastOps: Map<string, Map<string, { firstOp: string, lastOp: string }>>, opInfo: Map<string, OperationEntity>, month: number, year: number, opCategory?: OperationCategoryEnum): Promise<PoOperationCompletionModel[]> {

    // --------------- BLOCK 2 ---------------------------
    // for entire PO for the date
    const scheduleCompQtysTillNowQuery = this.trackInfoEnt.createQueryBuilder('track')
      .select('style, SUM(good_quantity) as good, SUM(rejected_quantity) as rej, SUM(rework_quantity) as rework, op_code, op_category, op_sequence')
      .where(` unit_id = '${unitId}' AND po_schedule_id IN(:...schIds) AND month = ${month} AND year = ${year}`, { schIds: poSchIds });
    if (opCategory) {
      scheduleCompQtysTillNowQuery.andWhere(` op_category = '${opCategory}' `);
    }
    scheduleCompQtysTillNowQuery.groupBy('unit_id, schedule, style, color, op_code, op_category')
      .orderBy('op_sequence');
    const scheduleCompQtysTillNowResult: { style: string, good: number, rej: number, rework: number, op_code: string, op_category: string, op_sequence: number }[] = await scheduleCompQtysTillNowQuery.getRawMany();

    const opCategoryOpInfoModel = new Map<string, PoOperationCompletionModel>();
    for (const rec of scheduleCompQtysTillNowResult) {
      rec.good = Number(rec.good);
      const currStyle = rec.style;
      const currOpCategory = rec.op_category;
      const currOpInfo = opInfo.get(rec.op_code);
      if (!opCategoryOpInfoModel.has(rec.op_category)) {
        const opSummaryModel = new PoOperationCompletionModel(rec.op_category, rec.op_code, currOpInfo.opCategory, currOpInfo.opSequence, 0, 0, 0, 0, 0, 0, 0, 0, null, null, 0);
        opCategoryOpInfoModel.set(currOpCategory, opSummaryModel);
      }

      // const firstOp =  firstAndLastOps.get(currStyle)?.get(currOpCategory)?.firstOp;
      // const lastOp = firstAndLastOps.get(currStyle)?.get(currOpCategory)?.lastOp;

      const firstOp = firstAndLastOps.get(this.STYLE_CONST)?.get(currOpCategory)?.firstOp;
      const lastOp = firstAndLastOps.get(this.STYLE_CONST)?.get(currOpCategory)?.lastOp;

      if (rec.op_code == firstOp) {
        opCategoryOpInfoModel.get(currOpCategory).thisMonthInput += rec.good;
      } else if (rec.op_code == lastOp) {
        opCategoryOpInfoModel.get(currOpCategory).thisMonthOutput += rec.good;
      }
    }

    const poOpCompModels: PoOperationCompletionModel[] = [];
    opCategoryOpInfoModel.forEach(r => {
      poOpCompModels.push(r);
    });
    return poOpCompModels;
  }


  async getWip(unitId: string, styles: string[], firstAndLastOps: Map<string, Map<string, { firstOp: string, lastOp: string }>>, filteredPoSchIds: number[]): Promise<Map<string, { totalInput: number, totalOutput: number, inTransitQty: number }>> {
    const wipMap = new Map<string, { totalInput: number, totalOutput: number, inTransitQty: number }>();

    const styleOpsInfo = await this.getStyleOperationOrder(styles, unitId);
    // get the total input and output qtys for the schedules 
    const wipQuery = this.trackInfoEnt.createQueryBuilder('track')
      .select(`style, SUM(good_quantity) as good, SUM(rejected_quantity) as rej, SUM(rework_quantity) as rework, op_code, op_category, op_sequence`)
      .where(` unit_id = '${unitId}' AND po_schedule_id IN(:...schIds)`, { schIds: filteredPoSchIds })
      .groupBy('unit_id, schedule, style, op_code, op_category')
      .orderBy(`style, op_code, op_sequence`);
    const wipResult: { style: string, good: number, rej: number, rework: number, op_code: string, op_category: string, op_sequence: number }[] = await wipQuery.getRawMany();
    const opQtysMap = new Map<string, Map<string, number>>();
    for (const rec of wipResult) {
      if (!opQtysMap.has(rec.style)) {
        opQtysMap.set(rec.style, new Map<string, number>());
      }
      if (!opQtysMap.get(rec.style).has(rec.op_code)) {
        opQtysMap.get(rec.style).set(rec.op_code, 0);
      }
      const preQty = opQtysMap.get(rec.style).get(rec.op_code);
      opQtysMap.get(rec.style).set(rec.op_code, Number(rec.good) + preQty);
    }
    for (const rec of wipResult) {
      const currStyle = rec.style;
      const currOpCategory = rec.op_category;
      // const firstOp =  firstAndLastOps.get(currStyle)?.get(currOpCategory)?.firstOp;
      // const lastOp = firstAndLastOps.get(currStyle)?.get(currOpCategory)?.lastOp;

      const firstOp = firstAndLastOps.get(this.STYLE_CONST)?.get(currOpCategory)?.firstOp;
      const lastOp = firstAndLastOps.get(this.STYLE_CONST)?.get(currOpCategory)?.lastOp;

      // to get the intransit qty, get the pre op of the current op
      const opInfo = styleOpsInfo?.get(rec.style)?.get(rec.op_code);
      const preOp = opInfo?.preOp;

      if (!wipMap.has(rec.op_category)) {
        wipMap.set(rec.op_category, { totalInput: 0, totalOutput: 0, inTransitQty: 0 });
      }
      if (rec.op_code == firstOp) {
        const inTrans = opQtysMap?.get(rec.style)?.get(preOp) ?? 0;
        wipMap.get(rec.op_category).totalInput += Number(rec.good);
        // Intransit qty must be only mapped to the first operation
        wipMap.get(rec.op_category).inTransitQty += Number(inTrans) - Number(rec.good);
      } else if (rec.op_code == lastOp) {
        wipMap.get(rec.op_category).totalOutput += Number(rec.good);
      }
    }
    return wipMap;
  }


  async getPreOpCategoryLastOpQty(unitId: string, firstAndLastOps: Map<string, Map<string, { firstOp: string, lastOp: string }>>, filteredPoSchIds: number[]): Promise<Map<string, { totalInput: number, totalOutput: number }>> {
    const wipMap = new Map<string, { totalInput: number, totalOutput: number }>();
    // get the total input and output qtys for the schedules 
    const wipQuery = this.trackInfoEnt.createQueryBuilder('track')
      .select(`style, SUM(good_quantity) as good, SUM(rejected_quantity) as rej, SUM(rework_quantity) as rework, op_code, op_category, op_sequence`)
      .where(` unit_id = '${unitId}' AND po_schedule_id IN(:...schIds)`, { schIds: filteredPoSchIds })
      .groupBy('unit_id, schedule, style, op_code, op_category');
    const wipResult: { style: string, good: number, rej: number, rework: number, op_code: string, op_category: string, op_sequence: number }[] = await wipQuery.getRawMany();
    for (const rec of wipResult) {
      const currStyle = rec.style;
      const currOpCategory = rec.op_category;

      // get the immediate pre operation for the current style and

      // const firstOp =  firstAndLastOps.get(currStyle)?.get(currOpCategory)?.firstOp;
      // const lastOp = firstAndLastOps.get(currStyle)?.get(currOpCategory)?.lastOp;

      const firstOp = firstAndLastOps.get(this.STYLE_CONST)?.get(currOpCategory)?.firstOp;
      const lastOp = firstAndLastOps.get(this.STYLE_CONST)?.get(currOpCategory)?.lastOp;

      if (!wipMap.has(rec.op_category)) {
        wipMap.set(rec.op_category, { totalInput: 0, totalOutput: 0 });
      }
      if (rec.op_code == firstOp) {
        wipMap.get(rec.op_category).totalInput += Number(rec.good);
      } else if (rec.op_code == lastOp) {
        wipMap.get(rec.op_category).totalOutput += Number(rec.good);
      }
    }
    return wipMap;
  }

  // NOT USED
  // helper for getting the first and last op for the operation and the category
  async getTheFirstAndLastOperationsForStyleInOpCategory(styles: string[], unit: string): Promise<Map<string, Map<string, { firstOp: string, lastOp: string }>>> {
    const opCatMap = new Map<string, Map<OperationCategoryEnum, { firstOp: string, lastOp: string, leastOpSeq: number, highOpSeq: number }>>();
    const query = this.styleOpEnt.createQueryBuilder('st')
      .select('style, op_code, op_category, op_sequence')
      .where(` unit_id = '${unit}' AND is_active = true AND style IN (:...styles)`, { styles: styles })
      .orderBy('style, op_category, op_sequence');

    const styleOps: { style: string, op_code: string, op_category: OperationCategoryEnum, op_sequence: number }[] = await query.getRawMany();
    // iterate and construct the map
    styleOps.forEach(rec => {
      if (!opCatMap.has(rec.style)) {
        opCatMap.set(rec.style, new Map<OperationCategoryEnum, { firstOp: string, lastOp: string, leastOpSeq: number, highOpSeq: number }>());
      }
      if (!opCatMap.get(rec.style).has(rec.op_category)) {
        opCatMap.get(rec.style).set(rec.op_category, { firstOp: '', lastOp: '', leastOpSeq: 999, highOpSeq: 0 });
      }
      const preLSeq = opCatMap.get(rec.style).get(rec.op_category).leastOpSeq;
      const preHSeq = opCatMap.get(rec.style).get(rec.op_category).highOpSeq;

      const nPreLSeq = Math.min(preLSeq, rec.op_sequence);
      const nPreHSeq = Math.max(preHSeq, rec.op_sequence);
      opCatMap.get(rec.style).get(rec.op_category).leastOpSeq = nPreLSeq;
      opCatMap.get(rec.style).get(rec.op_category).highOpSeq = nPreHSeq;

      if (nPreLSeq == rec.op_sequence) {
        opCatMap.get(rec.style).get(rec.op_category).firstOp = rec.op_code;
      }
      if (nPreHSeq == rec.op_sequence) {
        opCatMap.get(rec.style).get(rec.op_category).lastOp = rec.op_code;
      }
    });
    return opCatMap;
  }

  // helper for getting the first and last op for the operation and the category
  async getTheFirstAndLastOperationsForOpCategory(styles: string[], unit: string): Promise<Map<string, Map<string, { firstOp: string, lastOp: string }>>> {
    const styleOpCatMap = new Map<string, Map<OperationCategoryEnum, { firstOp: string, lastOp: string, leastOpSeq: number, highOpSeq: number }>>();
    const opCatMap = new Map<OperationCategoryEnum, { firstOp: string, lastOp: string, leastOpSeq: number, highOpSeq: number }>();
    const operations: { op_code: string, op_category: OperationCategoryEnum, op_sequence: number }[] = await this.opEnt.createQueryBuilder('op')
      .select('op_code, op_category, op_sequence')
      .where(` unit_id = '${unit}' AND is_active = true`)
      .orderBy('op_category, op_sequence')
      .getRawMany();

    operations.forEach(rec => {
      if (!opCatMap.has(rec.op_category)) {
        opCatMap.set(rec.op_category, { firstOp: '', lastOp: '', leastOpSeq: 999, highOpSeq: 0 });
      }
      const preLSeq = opCatMap.get(rec.op_category).leastOpSeq;
      const preHSeq = opCatMap.get(rec.op_category).highOpSeq;

      const nPreLSeq = Math.min(preLSeq, rec.op_sequence);
      const nPreHSeq = Math.max(preHSeq, rec.op_sequence);
      opCatMap.get(rec.op_category).leastOpSeq = nPreLSeq;
      opCatMap.get(rec.op_category).highOpSeq = nPreHSeq;

      if (nPreLSeq == rec.op_sequence) {
        opCatMap.get(rec.op_category).firstOp = rec.op_code;
      }
      if (nPreHSeq == rec.op_sequence) {
        opCatMap.get(rec.op_category).lastOp = rec.op_code;
      }
    });
    styleOpCatMap.set(this.STYLE_CONST, opCatMap);
    return styleOpCatMap;
  }

  async getStyleOperationOrder(style: string[], unit: string): Promise<Map<string, Map<string, { opCode: string, opSeq: number, preOp: string, opCat: OperationCategoryEnum }>>> {
    const styleOpsMap = new Map<string, Map<string, { opCode: string, opSeq: number, preOp: string, opCat: OperationCategoryEnum }>>();
    const query = this.styleOpEnt.createQueryBuilder('st')
      .select('style, op_code, op_category, op_sequence')
      .where(` unit_id = '${unit}' AND is_active = true AND style IN (:...styles)`, { styles: style })
      .orderBy('style, op_sequence');
    const styleOps: { style: string, op_code: string, op_category: OperationCategoryEnum, op_sequence: number }[] = await query.getRawMany();
    // iterate and construct the map
    let preRec: { style: string, op_code: string, op_category: OperationCategoryEnum, op_sequence: number } = { style: '', op_code: '', op_category: null, op_sequence: 0 };
    styleOps.forEach(rec => {
      if (!styleOpsMap.has(rec.style)) {
        styleOpsMap.set(rec.style, new Map<string, { opCode: string, opSeq: number, preOp: string, opCat: OperationCategoryEnum }>);
      }
      if (!styleOpsMap.get(rec.style).has(rec.op_code)) {
        styleOpsMap.get(rec.style).set(rec.op_code, { opCode: rec.op_code, opSeq: rec.op_sequence, preOp: preRec.op_code, opCat: rec.op_category });
      }
      preRec = rec;
    });
    return styleOpsMap;
  }

  /**
   * SUSPECT
   * @param poSchIds 
   * @param date 
   * @param unitId 
   */
  async getPoListForTheDate(poSchIds: number[], date: string, unitId: string): Promise<PoInfoModel[]> {
    const filteredPoSchIds: number[] = [];
    const scheduleCompQtysTillNowQuery = this.trackInfoEnt.createQueryBuilder('track')
      .select('DISTINCT po_schedule_id')
      .where(` unit_id = '${unitId}'  AND date = '${date}'`)
      .andWhere(` po_schedule_id IN (:...ids)`, { ids: poSchIds })
      .groupBy('unit_id, schedule, style, color, op_code, op_category')
      .orderBy('op_sequence');
    // console.log(scheduleCompQtysTillNowQuery.getQuery());
    // return null;
    const scheduleCompQtysTillNowResult: { po_schedule_id: number }[] = await scheduleCompQtysTillNowQuery.getRawMany();
    scheduleCompQtysTillNowResult.forEach(r => {
      filteredPoSchIds.push(r.po_schedule_id);
    });
    const poInfoModels: PoInfoModel[] = [];
    if (filteredPoSchIds.length > 0) {
      // get the po info for the filtered sch ids
      const schedulesForPo: { id: number, po_description: string, schedule: string, style: string, color: string, quantity: number, destination: string, delivery_date: string, buyer_name: string }[] = await this.schEnt.createQueryBuilder('sch')
        .select(`sch.id, sch.schedule, sch.style, sch.color, sch.quantity, sch.destination, sch.delivery_date, buy.buyer_name, poh.po_description`)
        .leftJoin(PoHeaderEntity, 'poh', 'sch.po_header_id = poh.id AND sch.unit_id = poh.unit_id ')
        .leftJoin(BuyerEntity, 'buy', 'buy.id = poh.buyer_id AND buy.unit_id = poh.unit_id')
        .where(`sch.unit_id = '${unitId}' AND sch.id IN (:...filteredPoSchIds)`, { filteredPoSchIds: filteredPoSchIds })
        .getRawMany();
      // const schedulesForPo = await this.schEnt.find({ select: ['id', 'poHeaderId'], where: { unitId: unitId, poStatus: In([PoStatusEnum.OPEN, PoStatusEnum.INPROGRESS])} });
      // if(schedulesForPo.length == 0) {
      //   throw new ErrorResponse(0, 'Open POs not found');
      // }
      schedulesForPo.forEach(r => {
        poInfoModels.push(new PoInfoModel(r.po_description, r.po_description, r.schedule, r.delivery_date, r.buyer_name));
      });
    }
    return poInfoModels;
  }

  // helper for getting the ops info
  async getOpInfo(unitId: string): Promise<Map<string, OperationEntity>> {
    const opsInfoMap = new Map<string, OperationEntity>();
    const ops = await this.opEnt.find({ select: ['id', 'opCategory', 'opCode', 'opName', 'opSequence'], where: { unitId: unitId, isActive: true } });
    ops.forEach(rec => {
      opsInfoMap.set(rec.opCode, rec);
    });
    return opsInfoMap;
  }


  async getGtsEnabledUnits(): Promise<UnitResponse> {
    const unitModels: UnitModel[] = [];
    const units = await this.unitEnt.find({ where: { isActive: true} });
    if(units.length == 0) {
        throw new ErrorResponse(0, 'No units found');
    }
    units.forEach(u => {
        unitModels.push( new UnitModel(u.id, u.unitId, u.unitName) );
    });
    return new UnitResponse(true, 0, 'Units retrieved', unitModels);
  }
}

